import { useEffect } from 'react';
import { SECURITY_HEADERS, CSP_CONFIG } from '../utils/security';

/**
 * Component that sets security headers and meta tags
 * This should be used in the main App component or index.html
 */
export function SecurityHeaders() {
  useEffect(() => {
    // Set security headers via meta tags (for client-side)
    const setSecurityMeta = () => {
      // Content Security Policy
      const cspDirectives = Object.entries(CSP_CONFIG)
        .map(([directive, values]) => `${directive} ${values.join(' ')}`)
        .join('; ');
      
      let cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
      if (!cspMeta) {
        cspMeta = document.createElement('meta');
        cspMeta.setAttribute('http-equiv', 'Content-Security-Policy');
        document.head.appendChild(cspMeta);
      }
      cspMeta.setAttribute('content', cspDirectives);

      // Other security headers as meta tags
      const securityMetaTags = [
        { name: 'X-Content-Type-Options', content: 'nosniff' },
        { name: 'X-Frame-Options', content: 'DENY' },
        { name: 'X-XSS-Protection', content: '1; mode=block' },
        { name: 'Referrer-Policy', content: 'strict-origin-when-cross-origin' },
        { name: 'Permissions-Policy', content: 'camera=(), microphone=(), geolocation=()' },
      ];

      securityMetaTags.forEach(({ name, content }) => {
        let meta = document.querySelector(`meta[name="${name}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', name);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      });
    };

    setSecurityMeta();

    // Cleanup function
    return () => {
      // Remove security meta tags if needed
      const securityMetaNames = [
        'Content-Security-Policy',
        'X-Content-Type-Options',
        'X-Frame-Options',
        'X-XSS-Protection',
        'Referrer-Policy',
        'Permissions-Policy',
      ];

      securityMetaNames.forEach(name => {
        const meta = document.querySelector(`meta[name="${name}"], meta[http-equiv="${name}"]`);
        if (meta) {
          meta.remove();
        }
      });
    };
  }, []);

  return null; // This component doesn't render anything
}

/**
 * Hook to set security headers programmatically
 */
export function useSecurityHeaders() {
  useEffect(() => {
    // Set security headers via meta tags
    const setSecurityMeta = () => {
      // Content Security Policy
      const cspDirectives = Object.entries(CSP_CONFIG)
        .map(([directive, values]) => `${directive} ${values.join(' ')}`)
        .join('; ');
      
      let cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
      if (!cspMeta) {
        cspMeta = document.createElement('meta');
        cspMeta.setAttribute('http-equiv', 'Content-Security-Policy');
        document.head.appendChild(cspMeta);
      }
      cspMeta.setAttribute('content', cspDirectives);

      // Other security headers as meta tags
      const securityMetaTags = [
        { name: 'X-Content-Type-Options', content: 'nosniff' },
        { name: 'X-Frame-Options', content: 'DENY' },
        { name: 'X-XSS-Protection', content: '1; mode=block' },
        { name: 'Referrer-Policy', content: 'strict-origin-when-cross-origin' },
        { name: 'Permissions-Policy', content: 'camera=(), microphone=(), geolocation=()' },
      ];

      securityMetaTags.forEach(({ name, content }) => {
        let meta = document.querySelector(`meta[name="${name}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', name);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      });
    };

    setSecurityMeta();
  }, []);
}

/**
 * Component that adds security features to the app
 */
export function SecurityProvider({ children }: { children: React.ReactNode }) {
  useSecurityHeaders();
  
  return <>{children}</>;
}


